/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, mergeMap, observeOn, skip, switchMap, take } from 'rxjs/operators';
import { faStar } from '@fortawesome/free-regular-svg-icons/faStar';
import { Store } from '@ngrx/store';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { GroupSharedService } from '../../../services/shared/group-shared/group-shared.service';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { GetAllUsersAdminPayload, UserActionTypes } from '../../../store/user/user.action';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { RoleInterface } from 'src/app/models/interface/role.interface';
import { UserSharedService } from 'src/app/services/shared/user-shared/user-shared.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Component:
 * Collaborator page displaying a list of collaborators of the group;
 * Can be found: {uri}/groups/{{group_id}}/collaborators
 */
@Component({
  selector: 'app-group-collaborator',
  templateUrl: './group-collaborator.component.html',
  styleUrls: ['./group-collaborator.component.scss'],
})
export class GroupCollaboratorComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faSearch = faSearch;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faStar = faStar;
  faEnvelope = faEnvelope;
  faTrash = faTrash;

  public profile: ProfileInterface;

  // Data provided by StudyService
  public collaborators: UserInterface[] = [];
  public eCoaches: UserInterface[];
  private myCollaborators: UserInterface[];
  private myNonCollaborators: UserInterface[];

  // Filter
  public filter = { searchFilter: '' };

  // List of roles
  public roles = ['study.ecoachmanager', 'study.ecoach', 'study.access'] as const;

  public isLoading$: Observable<boolean>;
  public isCollapse$: Observable<boolean>;

  public collaboratorsData$: Observable<PaginationData<UserInterface>>;
  public collaborators$: Observable<UserInterface[]>;
  public pagedCollaborators$: Observable<UserInterface[]>;

  public isManager$: Observable<boolean>;
  public isOrganisationManager$: Observable<boolean>;

  // Translation
  public param = { intervention_name: '...' };

  public profile$: Observable<ProfileInterface>;
  private profileRoles$: Observable<RoleInterface[]>;

  private isAdminSubject = new BehaviorSubject<boolean>(false);

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  // Study ID and loading status for this child component
  private study: StudyInterface;

  private searchTextSubject = new BehaviorSubject<string>('');
  private pagedCollaboratorsSubject = new BehaviorSubject<UserInterface[]>([]);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private collaboratorsDataSubject = new BehaviorSubject<PaginationData<UserInterface>>({ items: [] });
  private collaboratorsSubject = new BehaviorSubject<UserInterface[]>([]);
  private isManagerSubject = new BehaviorSubject<boolean>(false);
  private isOrganisationManagerSubject = new BehaviorSubject<boolean>(false);

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;

  private users$: Observable<UserInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private groupSharedService: GroupSharedService,
    private userSharedService: UserSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private translateService: TranslateService,
    private store: Store<{
      myProfile: ProfileInterface;
      myRoles: RoleInterface[];
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
      users: UserInterface[];
    }>,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.pagedCollaborators$ = this.pagedCollaboratorsSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.collaboratorsData$ = this.collaboratorsDataSubject.asObservable();
    this.collaborators$ = this.collaboratorsSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isOrganisationManager$ = this.isOrganisationManagerSubject.asObservable();
    this.profile$ = store.select('myProfile');
    this.profileRoles$ = this.store.select('myRoles');
    this.users$ = this.store.select('users');
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    // Sharing parent values: profileRoles and myCollaborators
    this.subscriptions.push(
      this.profileRoles$.pipe(filter((roles) => roles.length > 0)).subscribe({
        next: (roles) => {
          this.isAdminSubject.next(this.helperService.hasRoles(roles, /admin$/));
        },
        error: () => {
          this.isAdminSubject.next(false);
        },
      }),
    );
    this.subscriptions.push(
      this.userSharedService.collaborators$
        .pipe(
          filter((myCollaborators) => myCollaborators !== null),
        )
        .subscribe({
          next: (myCollaborators) => {
            this.myCollaborators = myCollaborators;
          },
          error: (error) => {
            throwError(() => new Error('Error in setup:')).pipe(observeOn(error));
          },
        }),
    );

    this.subscriptions.push(
      this.groupSharedService.isOrganisationManager$.subscribe(
        (value) => this.isOrganisationManagerSubject.next(value),
      ),
    );

    // Sharing parent values: id and study name
    this.subscriptions.push(
      this.groupSharedService.sourceGroup$
        .pipe(
          filter((study) => study !== null),
          switchMap((study) => {
            this.study = study;
            this.param = { intervention_name: study.attributes.name };
            return this.profile$.pipe(
              filter((user) => user !== null),
              take(1),
            );
          }),
        )
        .subscribe({
          next: (profile) => {
            this.profile = profile;
            this.applyCollaboratorFilter();
          },
          error: (error) => {
            throwError(() => new Error('Error in setup:')).pipe(observeOn(error));
          },
        }),
    );
  }

  public applyCollaboratorFilter(setFirst?: boolean): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadCollaboratorList(setFirst).subscribe(() => {
        this.isLoadingSubject.next(false);
      }),
    );
  }

  public updatePagedCollaborators(collaborators: UserInterface[]) {
    if (collaborators) {
      this.pagedCollaboratorsSubject.next(collaborators);
    }
  }

  public reloadCollaboratorList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;

    this.store.dispatch({
      type: StudyActionTypes.getCollaboratorsType,
      payload: { studyId: this.study.id, include: 'roles' },
    });
    this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.study.id));
    const studyCollaborators = this.collaboratorsOfStudy$.pipe(skip(1), take(1));

    let allECoaches: Observable<UserInterface[]>;
    if (this.isAdminSubject.value) {
      const payload: GetAllUsersAdminPayload = {
        roles: 'ecoach',
        isVerified: 1,
      };
      this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
      allECoaches = this.users$.pipe(skip(1), take(1));
    } else {
      allECoaches = of([]);
    }

    const reqs = { studyCollaborators, allECoaches };
    return forkJoin(reqs).pipe(
      mergeMap((results) => {
        this.collaborators = results.studyCollaborators.collaborators;
        this.eCoaches = this.helper.getVerifiedEditorsNotInStudy(results.allECoaches, this.collaborators);
        if (this.myCollaborators) {
          this.myNonCollaborators = this.helper.getVerifiedEditorsNotInStudy(
            this.myCollaborators,
            this.collaborators,
          );
        }
        const profileId = this.profile.id.toString();
        const myself = this.collaborators.find((user) => user.id.toString() === profileId);
        if (myself) {
          const highestRole = this.helper.getHighestRoleOfCollaborator(myself);
          if (highestRole === 'study.ecoachmanager' || highestRole === 'study.owner') {
            this.isManagerSubject.next(true);
          }
        }
        // If user is not manager - they can only see study.ecoachmanager, study.owner and themselves
        if (!this.isManagerSubject.value && !this.isOrganisationManagerSubject.value) {
          this.collaborators = this.collaborators.filter(
            (user) =>
               this.helperService.hasRoles(user.relationships?.roles?.data, /study\.(ecoachmanager|owner)$/) || user.id.toString() === profileId,
          );
        }
        return this.performFilterAndPagination(!!setFirst, pageIndex, pageSize);
      }),
      catchError((error) => {
        throwError(() => new Error('ForkJoin error')).pipe(observeOn(error));
        return this.performFilterAndPagination(!!setFirst, pageIndex, pageSize);
      }),
    );
  }

  public openDialogGroupCollaboratorOrganisationManager(): void {
    this.helperDialog
      .openDialogGroupCollaboratorOrganisationManager(
        this.study,
        this.collaborators,
        this.eCoaches,
        this.myNonCollaborators,
        this.isAdminSubject.value,
      )
      .afterClosed()
      .subscribe(() => this.applyCollaboratorFilter());
  }

  public openDialogGroupCollaboratorAdd(): void {
    this.helperDialog
      .openDialogGroupCollaboratorAdd(
        this.param,
        this.study.id,
        this.collaborators,
        this.eCoaches,
        this.myNonCollaborators,
        this.isAdminSubject.value,
      )
      .afterClosed()
      .subscribe(() => this.applyCollaboratorFilter());
  }

  public openDialogGroupCollaboratorEdit(): void {
    this.helperDialog
      .openDialogGroupCollaboratorEdit(this.param, this.study.id, this.collaborators)
      .afterClosed()
      .subscribe(() => this.applyCollaboratorFilter());
  }

  public openDialogGroupCollaboratorRemove(user: UserInterface): void {
    this.helperDialog
      .openDialogGroupCollaboratorRemove(this.param, this.study.id, user)
      .afterClosed()
      .subscribe(() => this.applyCollaboratorFilter());
  }

  public performFilterAndPagination(setFirst: boolean, pageIndex: number, pageSize: number): Observable<boolean> {
    return this.helper
      .searchUsersByInput(this.filter.searchFilter, this.searchTextSubject, this.collaborators, this.collaboratorsSubject)
      .pipe(
        mergeMap(() =>
          this.helper.setPagedContent(
            this.collaboratorsSubject,
            this.pagedCollaboratorsSubject,
            setFirst,
            pageIndex,
            pageSize,
            this.collaboratorsDataSubject,
          ),
        ),
      );
  }

  getCollaboratorEmail(collaborator: UserInterface): string {
    return collaborator.attributes.email || this.translateService.instant('intervention-collaborator.no_email');
  }

  getCollaboratorFullName(collaborator: UserInterface): string {
    if (collaborator.attributes.firstname && collaborator.attributes.lastname) {
      return collaborator.attributes.firstname + ' ' + collaborator.attributes.lastname;
    }
    return this.translateService.instant('intervention-collaborator.no_names');
  }

  getCollaboratorName(collaborator: UserInterface): string {
    return collaborator.attributes.name || this.translateService.instant('intervention-collaborator.no_username');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
